/**
 * PartDSignature - TASK-371 (extracted from FormAForm)
 * Part D: Digital signature capture — signatory name, date, and signature pad.
 */

import React from 'react';
import { PenTool, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import type { FormAFormData } from './FormAForm';

type SignatureData = FormAFormData['signature'];

interface PartDSignatureProps {
  data: SignatureData;
  onChange: (field: string, value: any) => void;
  errors: Record<string, string>;
  allDeclarationsChecked: boolean;
}

import { fieldError } from '../../../utils/formHelpers';

export const PartDSignature: React.FC<PartDSignatureProps> = ({
  data,
  onChange,
  errors,
  allDeclarationsChecked,
}) => (
  <div className="px-4 pb-4 space-y-4">
    {!allDeclarationsChecked && (
      <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
        <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
        <p className="text-xs text-amber-700">
          All CDB 20 declarations in Part C must be confirmed before signing.
        </p>
      </div>
    )}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Signatory Name *
        </label>
        <input
          type="text"
          value={data.signatory_name || ''}
          onChange={(e) => onChange('signatory_name', e.target.value)}
          className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors['signature.signatory_name'] ? 'border-red-300' : 'border-gray-200'}`}
          placeholder="Full name of signatory"
        />
        {fieldError(errors, 'signature.signatory_name')}
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Date *
        </label>
        <input
          type="date"
          value={data.date || ''}
          onChange={(e) => onChange('date', e.target.value)}
          className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors['signature.date'] ? 'border-red-300' : 'border-gray-200'}`}
        />
        {fieldError(errors, 'signature.date')}
      </div>
    </div>

    {/* Signature placeholder */}
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        Digital Signature
      </label>
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50">
        <PenTool className="w-8 h-8 text-gray-300 mx-auto mb-2" />
        <p className="text-sm text-gray-500">
          {data.signature_data
            ? 'Signature captured'
            : 'Click to add digital signature'}
        </p>
        <button
          type="button"
          onClick={() => {
            onChange('signature_data', `signed_${Date.now()}`);
            toast.success('Signature captured');
          }}
          disabled={!allDeclarationsChecked}
          className="mt-2 text-sm text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed"
        >
          {data.signature_data ? 'Re-sign' : 'Sign here'}
        </button>
      </div>
    </div>
  </div>
);

export default PartDSignature;
